import { isAxiosError } from 'axios';

/**
 * Shared server-error extraction for the Ground Truth editors.
 *
 * All three layer editors (page splits, sheet splits, rows) PUT to endpoints
 * that reject bad input with a FastAPI **422** whose `detail` is either a plain
 * string or the standard list-of-error-dicts shape. This pulls the most useful
 * human-readable message out of an Axios error and falls back to a generic.
 *
 * `entityType` (plural, lower-case — e.g. "page splits", "sheets", "rows") is
 * woven into the generic messages so each editor keeps its own wording without
 * re-implementing the extraction. The layer-5 rows endpoint additionally tags
 * its per-row errors with `row_index`, which is surfaced as "Row N rejected".
 */
export function getServerErrorMessage(
    error: unknown,
    entityType = 'changes',
): string {
    if (isAxiosError(error)) {
        const detail = error.response?.data?.detail;
        if (typeof detail === 'string') return detail;
        if (Array.isArray(detail) && detail[0]) {
            const first = detail[0];
            const msg = first.msg ?? 'invalid input';
            // Layer-5 rows tag the offending row; splits/sheets don't.
            const rowIndex = first.row_index;
            if (rowIndex != null) {
                return `Row ${rowIndex + 1} rejected: ${msg}`;
            }
            return `Server rejected the ${entityType}: ${msg}`;
        }
    }
    return `Failed to save ${entityType}.`;
}
